import React, { useState, useEffect } from "react";
import TextInput from "../../components/Reusable/TextInput";
import SelectInput from "../../components/Reusable/SelectInput";
import DateInput from "../../components/Reusable/DateInput";
import {
  _useGetAllJobGrades,
  _useGetEmployeeById,
  useGetAllEmployeesPaginated,
  useGetAllJobGrades,
} from "../../hooks/useUsers";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import CheckboxInput from "../../components/Reusable/CheckboxInput";
import SelectableItemInput from "../../components/Reusable/SelectableItemInput";
import {
  _useGetAllAllowanceTypesPaginated,
  _useGetAllDeductionTypesPaginated,
  _useGetEmployeeSalaryRecordById,
  useCreateEmployeeSalaryRecord,
  useUpdateEmployeeSalaryRecord,
} from "../../hooks/usePayroll";
import {
  formatDateOnly,
  successActionPopup,
  successPopup,
} from "../../util/common";
import NumberInput from "../../components/Reusable/NumberInput";

function AddSalaryRecordLayout() {
  const [salaryRecord, setSalaryRecord] = useState({
    EmployeeID: null,
    BasicSalary: "",
    PayType: "Monthly",
    PayFrequency: "SemiMonthly",
    EmployeeJobGradeRecordID: null,
    EffectiveDate: formatDateOnly(new Date()),
    FromDate: formatDateOnly(new Date()),
    ToDate: formatDateOnly(new Date()),
    CurrencyCode: "PHP",
    ExchangeRate: "59",
    TaxFlag: true,
    TaxExempt: false,
    SalaryReceived: "Gross",
    OTComputationID: null,
    ChangeReason: "",
    ChangeRemarks: "",
    Current: true,
    Locked: false,
    IsFixedSalary: false,
    Version: 1,
    SupersededBy: null,
    Active: true,
    Status: true,
    VariableEarnings: [],
    VariableDeductions: [],
  });

  const navigate = useNavigate();
  const [isEditUser, setIsEditUser] = useState(false);
  const [editUserID, setEditUserID] = useState(null);

  const [employeeRecords, setEmployeeRecords] = useState([]);
  const [jobGradeRecords, setJobGradeRecords] = useState([]);
  const [allowancesRecords, setAllowancesRecords] = useState([]);
  const [deductionsRecords, setDeductionsRecords] = useState([]);

  const [editedFields, setEditedFields] = useState({});

  const employees = useGetAllEmployeesPaginated();
  const createSalaryRecord = useCreateEmployeeSalaryRecord();
  const updateSalaryRecord = useUpdateEmployeeSalaryRecord();

  const {
    data: allowances,
    isLoading: allowanceIsLoading,
    isError: allowanceIsError,
  } = _useGetAllAllowanceTypesPaginated({
    page: 1,
    pageSize: 150,
  });

  const {
    data: deductions,
    isLoading: deductionIsLoading,
    isError: deductionIsError,
  } = _useGetAllDeductionTypesPaginated({
    page: 1,
    pageSize: 150,
  });

  const {
    data: jobGrades,
    isLoading: jobGradeIsLoading,
    isError: jobGradeIsError,
  } = _useGetAllJobGrades();

  const { data: employeeData, isLoading: employeeIsLoading } =
    _useGetEmployeeById(salaryRecord.EmployeeID);

  const fetchDropdownDatas = async () => {
    employees.mutate(
      {
        page: 1,
        pageSize: 150,
      },
      {
        onSuccess: async (result) => {
          if (!result.success) return;

          const records = result.data.data.map((r) => ({
            id: r.id,
            name: `${r.EmployeeCode} - ${r.FullName}`,
          }));

          setEmployeeRecords(records);
        },
      }
    );
  };

  useEffect(() => {
    fetchDropdownDatas();
  }, []);

  useEffect(() => {
    if (!allowances?.success || !deductions?.success || !jobGrades?.success)
      return;

    const allowanceRecords = allowances.data.data.map((r) => ({
      id: r.id,
      name: r.Code,
    }));

    const deductionRecords = deductions.data.data.map((r) => ({
      id: r.id,
      name: r.Code,
    }));

    const jobGradeRecords = jobGrades.data.map((r) => ({
      id: r.id,
      name: `${r.GradeCode} - ${r.GradeName}`,
      ...r,
    }));

    setAllowancesRecords(allowanceRecords);
    setDeductionsRecords(deductionRecords);
    setJobGradeRecords(jobGradeRecords);
  }, [allowances, deductions, jobGrades]);

  const { id } = useParams();

  const {
    data: _salaryRecordData,
    isLoading: _salaryRecordIsLoading,
    isError: __salaryRecordIsError,
  } = _useGetEmployeeSalaryRecordById(id);

  useEffect(() => {
    if (id) {
      setIsEditUser(true);
      setEditUserID(id);
    }
  }, []);

  useEffect(() => {
    if (_salaryRecordData?.success && _salaryRecordData?.data) {
      const record = {
        ..._salaryRecordData.data,
        EffectiveDate: formatDateOnly(_salaryRecordData.data.EffectiveDate),
        FromDate: formatDateOnly(_salaryRecordData.data.FromDate),
        ToDate: formatDateOnly(_salaryRecordData.data.ToDate),
        VariableEarnings: (_salaryRecordData.data.VariableEarnings ?? []).map(
          (v) => ({
            code: v?.AllowanceTypeID,
            amount: v?.Amount,
            isTaxable: v?.Taxable,
            label: v?.AllowanceType?.Code,
          })
        ),

        VariableDeductions: (
          _salaryRecordData.data.VariableDeductions ?? []
        ).map((v) => ({
          code: v?.DeductionTypeID,
          amount: v?.Amount,
          label: v?.DeductionType?.Code,
        })),
      };

      setSalaryRecord(record);
    }
  }, [_salaryRecordData]);

  const handleChange = (field, value) => {
    setSalaryRecord((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (isEditUser) {
      setEditedFields((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleCheckboxChange = (field, value) => {
    setSalaryRecord((prev) => {
      const updated = { ...prev, [field]: value };

      if (field === "TaxFlag" && value) {
        updated.TaxExempt = false;
        updated.IsFixedSalary = false;
      }

      if ((field === "TaxExempt" || field === "IsFixedSalary") && value) {
        updated.TaxFlag = false;
      }

      return updated;
    });
  };

  const handleSubmitSalaryRecord = async () => {
    if (isEditUser) {
      updateSalaryRecord.mutate(
        {
          id,
          payload: {
            ...salaryRecord,
            BasicSalary: parseFloat(salaryRecord.BasicSalary),
          },
        },
        {
          onSuccess: (data) => {
            if (data.success) {
              toast.success(data.message);
              navigate("/SalaryRecord");
            }
          },
        }
      );
    } else {
      createSalaryRecord.mutate(
        { ...salaryRecord, BasicSalary: parseFloat(salaryRecord.BasicSalary) },
        {
          onSuccess: async (data) => {
            if (data.success) {
              toast.success(data.message);
              navigate("/SalaryRecord");
            }
          },
        }
      );
    }
  };

  useEffect(() => {
    if (!employeeData) return;

    if (
      employeeData.data.JobGradeRecords &&
      Array.isArray(employeeData.data.JobGradeRecords)
    ) {
      const currentJobGrade = employeeData.data.JobGradeRecords.find(
        (r) => r.Current
      );

      if (currentJobGrade) {
        handleChange("EmployeeJobGradeRecordID", currentJobGrade.JobGrade.id);
      }
    }
  }, [employeeData]);

  const isSubmitDisabled = isEditUser && Object.keys(editedFields).length === 0;

  return (
    <div className="add-user-page">
      <div className="form-container">
        <h2>{isEditUser ? "Edit" : "Add"} Salary Record</h2>

        <form className="add-user-form">
          {/* ================= EMPLOYEE & SALARY DETAILS ================= */}
          <section>
            <h3>Employee & Salary Details</h3>

            <div className="form-row">
              <SelectInput
                label="Employee"
                choices={employeeRecords}
                value={salaryRecord.EmployeeID}
                onChange={(val) => handleChange("EmployeeID", parseInt(val))}
                readOnly={isEditUser}
              />

              <SelectInput
                label="Job Grade (Edit in User Module)"
                choices={jobGradeRecords}
                value={salaryRecord.EmployeeJobGradeRecordID}
                onChange={(val) =>
                  handleChange("EmployeeJobGradeRecordID", val)
                }
                readOnly={true}
              />
            </div>

            <div className="form-row">
              <NumberInput
                label="Basic Salary"
                placeholder="25000.00"
                value={salaryRecord.BasicSalary}
                onChange={(val) => handleChange("BasicSalary", val)}
              />

              <SelectInput
                label="Pay Frequency"
                choices={[
                  { id: "Monthly", name: "Monthly" },
                  { id: "SemiMonthly", name: "Semi-Monthly" },
                  { id: "Weekly", name: "Weekly" },
                  { id: "Daily", name: "Daily" },
                  { id: "Hourly", name: "Hourly" },
                ]}
                value={salaryRecord.PayFrequency}
                onChange={(val) => handleChange("PayFrequency", val)}
              />
            </div>

            <div className="form-row">
              <SelectInput
                label="Salary Basis"
                choices={[
                  { id: "Gross", name: "Gross" },
                  { id: "Net", name: "Net" },
                ]}
                value={salaryRecord.SalaryReceived}
                onChange={(val) => handleChange("SalaryReceived", val)}
              />
            </div>
          </section>

          {/* ================= EFFECTIVITY PERIOD ================= */}
          <section>
            <h3>Salary Effectivity</h3>

            <div className="form-row">
              <DateInput
                label="Effective Date"
                value={salaryRecord.EffectiveDate}
                onChange={(val) => handleChange("EffectiveDate", val)}
              />
            </div>

            <div className="form-row">
              <DateInput
                label="From Date"
                value={salaryRecord.FromDate}
                onChange={(val) => handleChange("FromDate", val)}
              />

              <DateInput
                label="To Date"
                value={salaryRecord.ToDate}
                onChange={(val) => handleChange("ToDate", val)}
              />
            </div>
          </section>

          {/* ================= CURRENCY & EXCHANGE ================= */}
          <section>
            <h3>Currency Information</h3>

            <div className="form-row">
              <TextInput
                label="Currency Code"
                placeholder="PHP"
                value={salaryRecord.CurrencyCode || "PHP"}
                onChange={(val) => handleChange("CurrencyCode", val)}
              />

              <TextInput
                label="Exchange Rate (USD)"
                placeholder="59.002"
                value={salaryRecord.ExchangeRate}
                onChange={(val) => handleChange("ExchangeRate", val)}
              />
            </div>
          </section>

          {/* ================= TAX INFORMATION ================= */}
          <section>
            <h3>Tax Information</h3>

            <div className="form-row">
              <CheckboxInput
                label="Taxable"
                checked={salaryRecord.TaxFlag}
                onChange={(_, val) => handleCheckboxChange("TaxFlag", val)}
              />

              <CheckboxInput
                label="Tax Exempt"
                checked={salaryRecord.TaxExempt}
                onChange={(_, val) => handleCheckboxChange("TaxExempt", val)}
              />

              <CheckboxInput
                label="Fixed Salary"
                checked={salaryRecord.IsFixedSalary}
                onChange={(_, val) =>
                  handleCheckboxChange("IsFixedSalary", val)
                }
              />
            </div>

            <div className="form-row">
              <SelectInput
                label="Tax Computation"
                choices={[]}
                value={salaryRecord.OvertimeComputation}
                onChange={(val) => handleChange("OvertimeComputation", val)}
              />
            </div>
          </section>

          {/* ================= OVERTIME ================= */}
          <section>
            <h3>Overtime Configuration</h3>

            <div className="form-row">
              <SelectInput
                label="Overtime Computation"
                choices={[]}
                value={salaryRecord.OTComputationID}
                onChange={(val) => handleChange("OTComputationID", val)}
              />
            </div>
          </section>

          {/* ================= ALLOWANCES INFORMATION ================= */}
          <section>
            <h3>Allowances Information</h3>

            {/* Allowance Type Selection */}
            <div className="form-row">
              <SelectableItemInput
                title="Allowances Information"
                items={allowancesRecords}
                value={salaryRecord.VariableEarnings}
                onChange={(val) => handleChange("VariableEarnings", val)}
                showTaxable
              />
            </div>
          </section>

          {/* ================= DEDUCTION INFORMATION ================= */}
          <section>
            <h3>Deductions Information</h3>

            <div className="form-row">
              <SelectableItemInput
                title="Deduction Information"
                items={deductionsRecords}
                value={salaryRecord.VariableDeductions}
                onChange={(val) => handleChange("VariableDeductions", val)}
              />
            </div>
          </section>

          {/* ================= REMARKS ================= */}
          <section>
            <h3>Change Details</h3>

            <div className="form-row">
              <TextInput
                label="Change Reason"
                placeholder="Salary adjustment, promotion, correction, etc."
                value={salaryRecord.ChangeReason}
                onChange={(val) => handleChange("ChangeReason", val)}
              />
            </div>

            <div className="form-row">
              <TextInput
                label="Change Remarks"
                placeholder="Additional notes"
                value={salaryRecord.ChangeRemarks}
                onChange={(val) => handleChange("ChangeRemarks", val)}
              />
            </div>
          </section>

          {/* ================= ACTIONS ================= */}
          <div className="form-actions">
            <button
              type="button"
              disabled={isSubmitDisabled}
              onClick={handleSubmitSalaryRecord}
            >
              Submit Salary Record
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddSalaryRecordLayout;
